<template>
  <div class="page">
    <Banner v-if="webAssistantAppInstallError" :show-overlay="true">
      <div class="banner-text">There's a problem with your Web Assistant config. Let's set it up again</div>
      <n-button @click="reinstall" variant="primary" :loading="reinstallInProgress"> Reconfigure </n-button>
    </Banner>

    <PageHeader
      title="Web Assistant"
      description="Support your customers easily and effectively with a customizable web assistant"
    />

    <n-loading-spinner v-if="!webAssistantAppLoaded" />
    <section class="section content-container" v-if="webAssistantAppLoaded">
      <div class="container is-fluid">
        <div class="columns">
          <div class="column is-8">
            <CollapsibleSection :index="1" v-model="activeCollapseId">
              <template v-slot:title>Style your widget</template>
              <template v-slot:subtitle>Style the web assistant widget to match your site</template>
              <div class="columns">
                <div class="column is-6 columns__column--no-top-padding">
                  <strong class="label">Colours</strong>
                  <div class="field">
                    <n-colour-picker id="brand-colour" v-model="viewModel.theme.brandColour" label="Brand Colour">
                    </n-colour-picker>
                  </div>
                  <div class="field">
                    <n-colour-picker
                      id="sent-message-colour"
                      v-model="viewModel.theme.sentMessageColour"
                      label="Sent Message Colour"
                    >
                    </n-colour-picker>
                  </div>
                  <div class="field">
                    <n-colour-picker id="action-colour" v-model="viewModel.theme.actionColour" label="Action Colour">
                    </n-colour-picker>
                  </div>

                  <strong class="label">Logo</strong>
                  <div class="field">
                    <label class="label">Upload a logo to display on the homescreen</label>
                    <ImageUpload
                      :accept="'.png,.jpg,.jpeg'"
                      :previewWidth="300"
                      :previewHeight="48"
                      :image="viewModel.imageUrl"
                      :disabled="webAssistantAppInstallError"
                      v-on:selected="onImageSelected()"
                      v-on:uploaded="onImageUploaded($event)"
                      v-on:cleared="onImageCleared()"
                    >
                    </ImageUpload>
                  </div>

                  <strong class="label">Launcher</strong>
                  <div class="field">
                    <n-toggle
                      id="use-default-launcher"
                      name="use-default-launcher"
                      v-model="viewModel.useDefaultLauncher"
                      label="Show contacts the launcher"
                    ></n-toggle>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            <CollapsibleSection :index="2" v-model="activeCollapseId">
              <template v-slot:title>Say hello</template>
              <template v-slot:subtitle>Greet your visitors and introduce yourself</template>
              <div class="columns">
                <div class="column is-6">
                  <div class="field">
                    <n-text-input
                      v-model="viewModel.greetingText"
                      label="Greeting"
                      id="greeting"
                      @input="$v.greetingText.$touch()"
                      :error-message="greetingErrorMessage"
                    ></n-text-input>
                  </div>
                  <div class="field">
                    <n-text-input
                      v-model="viewModel.introductionText"
                      label="Introduction"
                      id="introduction"
                      @input="$v.introductionText.$touch()"
                      :error-message="introductionErrorMessage"
                    ></n-text-input>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            <CollapsibleSection :index="3" v-model="activeCollapseId">
              <template v-slot:title>Set your availability</template>
              <template v-slot:subtitle>Inform your visitors how long they can expect to wait</template>
              <div class="columns">
                <div class="column is-6">
                  Let your customers know how long they might be waiting for a response. This will be displayed on the
                  homescreen, and in the conversation header
                </div>
                <div class="column is-6">
                  <div class="field">
                    <n-radio-buttons v-model="viewModel.typicalResponseTime" :options="typicalResponseTimeOptions">
                    </n-radio-buttons>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            <CollapsibleSection :index="4" v-model="activeCollapseId">
              <template v-slot:title>Add homescreen apps</template>
              <template v-slot:subtitle>Enable your visitors to self serve before they start a conversation </template>
              <div class="columns">
                <div class="column">
                  <CapableAppPicker
                    :modalHeading="'Homescreen Apps'"
                    :capability="'homescreen'"
                    :selectedApps="viewModel.homescreenApps"
                    :configRequest="configRequest"
                    @change="homescreenAppsChanged"
                    @update="(app: ConfiguredAppIntegration, appIndex: number) => handleAppUpdate(app, appIndex)"
                  />
                </div>
              </div>
            </CollapsibleSection>

            <CollapsibleSection :index="5" v-model="activeCollapseId">
              <template v-slot:title>Embed in your website</template>
              <template v-slot:subtitle
                >Connecting Web Assistant lets you chat with people when they're on your website
              </template>
              <div class="field">
                <label class="label">Code Snippet</label>
                <ReadonlyCopyableField
                  :readonlyValue="getCodeSnippet()"
                  onCopyMessage="Copied snippet to clipboard"
                ></ReadonlyCopyableField>
                <p class="help">
                  Copy the snippet and add it to the
                  <strong>&lt;head&gt;</strong> section on all pages of your website
                </p>
              </div>
              <div class="field">
                <label class="label">Init Snippet</label>
                <ReadonlyCopyableField
                  :readonlyValue="getInitSnippet()"
                  onCopyMessage="Copied snippet to clipboard"
                ></ReadonlyCopyableField>
                <p class="help">
                  Copy the snippet and add it to the
                  <strong>&lt;body&gt;</strong> section on all pages of your website
                </p>
              </div>
            </CollapsibleSection>

            <CollapsibleSection :index="6" v-model="activeCollapseId">
              <template v-slot:title>Secure your Web Assistant</template>
              <template v-slot:subtitle>Domain whitelisting and identity verification</template>
              <div class="columns">
                <div class="column is-6">
                  Enter a list of domains which will contain the web assistant widget. Domains that are not listed here
                  will not be able to use the web assistant widget. If this field is blank, the widget can be added to
                  any domain.
                </div>
                <div class="column is-6">
                  <TagsInput placeholder="Enter a list of domains..." v-model="viewModel.whitelistedDomains">
                  </TagsInput>
                </div>
              </div>

              <div class="field">
                <label class="label">Identity Verification Secret</label>
                <ReadonlyCopyableField
                  :readonlyValue="identityVerificationSecret"
                  onCopyMessage="Copied snippet to clipboard"
                ></ReadonlyCopyableField>
                <p class="help">
                  Use this secret to sign the JWT when verifying your users with us. This should only be done server
                  side, so this value should never be exposed to front end applications.
                </p>
              </div>
            </CollapsibleSection>

            <n-button @click="update" variant="primary" :loading="updateInProgress" :disabled="$v.$invalid">
              Save
            </n-button>
          </div>
          <div class="column is-4 preview-column">
            <div class="preview-container">
              <div class="view-toggle-container">
                <WebAssistantViewToggle
                  :activeView="activeView"
                  @viewChange="(view: 'homescreen' | 'conversation') => (activeView = view)"
                />
              </div>
              <WebAssistantPreview
                :brandColour="viewModel.theme.brandColour"
                :outboundMessageColour="viewModel.theme.sentMessageColour"
                :actionColour="viewModel.theme.actionColour"
                :headerText="viewModel.headerText"
                :homescreenImageUrl="viewModel.imageUrl"
                :greetingText="viewModel.greetingText"
                :introductionText="viewModel.introductionText"
                :typicalResponseTime="viewModel.typicalResponseTime"
                :displayLauncher="viewModel.useDefaultLauncher"
                :homescreenApps="initialisedHomescreenApps"
                :isOpen="true"
                :messages="sampleMessages"
                :activeView="activeView"
                :disableInteraction="true"
                @load="messageLoad"
              ></WebAssistantPreview>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import WebAssistantPreview from '../components/WebAssistantPreview.vue';
import WebAssistantViewToggle from '../components/WebAssistantViewToggle.vue';
import ImageUpload from '../components/WebAssistantImageUpload.vue';
import AssetService from '../services/assets';
import { webAssistantApi } from '../services/webAssistant';
import { webAssistantAppService } from '../services/webAssistantAppService';
import { HomescreenAppInitViewModel, HomescreenAppViewModel, WebAssistantViewModel } from '../models';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import Toast from '@/shared/services/toast';
import Banner from '@/shared/components/Banner.vue';
import CollapsibleSection from '@/shared/components/CollapsibleSection.vue';
import CapableAppPicker from '@/shared/components/capableAppPicker/CapableAppPicker.vue';
import PageHeader from '@/shared/components/pageHeader/PageHeader.vue';
import ReadonlyCopyableField from '@/shared/components/ReadonlyCopyableField.vue';
import { HomescreenAppCache } from '../services/homescreenAppCache';
import TagsInput from '@/shared/components/TagsInput.vue';
import { TypicalResponseTimeOptions } from '@/features/webassistant/models';
import { ConversationMessage } from '../models/conversation-message';
import { MessageDirection } from '../models/messageDirection';
import { MessageStatus } from '../models/messageStatus';
import { useStore } from 'vuex';
import { computed, onMounted, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import { ConfiguredAppIntegration } from '@/features/apps/shared/models/configuredAppIntegration';
import VuelidateUtil from '@shared/util/vuelidateUtil';

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);

const viewModel = ref<WebAssistantViewModel>({
  theme: {
    brandColour: '#062B80',
    sentMessageColour: '#062B80',
    actionColour: '#062B80',
  },
  headerText: '',
  typicalResponseTime: 0,
  whitelistedDomains: [],
  homescreenApps: [],
  imageUrl: '',
  greetingText: 'Hi there 👋',
  introductionText: "We're here to help. Ask us anything",
  useDefaultLauncher: true,
});

const rules = {
  greetingText: { required, maxLength: maxLength(25) },
  introductionText: { required, maxLength: maxLength(40) },
};
const $v = useVuelidate(rules, viewModel);

const activeCollapseId = ref(1);
const identityVerificationSecret = ref('');
const initialisedHomescreenApps = ref<HomescreenAppInitViewModel[]>([]);
const imageUploading = ref(false);
const webAssistantAppLoaded = ref(false);
const webAssistantAppInstallError = ref(false);
const reinstallInProgress = ref(false);
const updateInProgress = ref(false);
const activeView = ref<'homescreen' | 'conversation'>('homescreen');
const codeSnippet = ref('');
const homescreenAppCache = ref<HomescreenAppCache>();

const typicalResponseTimeOptions = TypicalResponseTimeOptions;
const sampleMessages: ConversationMessage[] = [
  {
    id: '1',
    type: 'text',
    direction: MessageDirection.Inbound,
    status: MessageStatus.DeliverySucceeded,
    timestampUtc: new Date(),
    text: 'Hi there! Is there anything we can help you with?',
  },
  {
    id: '2',
    type: 'text',
    direction: MessageDirection.Outbound,
    status: MessageStatus.DeliverySucceeded,
    timestampUtc: new Date(),
    text: 'Hi, yes there is...',
  },
];

onMounted(async () => {
  if (!currentWorkspace.value) {
    await handleSeriousError('No workspace selected');
    return;
  }

  homescreenAppCache.value = new HomescreenAppCache();

  try {
    const response = await webAssistantApi.getInstalledApp(currentWorkspace.value.id);
    viewModel.value = response as WebAssistantViewModel;
    homescreenAppsChanged(viewModel.value.homescreenApps);
    identityVerificationSecret.value = response.identityVerificationSecret;
    webAssistantAppLoaded.value = true;
  } catch (error: any) {
    if (error.status === 404) {
      webAssistantAppInstallError.value = true;
      viewModel.value.headerText = currentWorkspace.value.name;
      webAssistantAppLoaded.value = true;
      return;
    }
    await handleSeriousError(error.message);
  }

  const data: any = await AssetService.loadCodeSnippet();
  codeSnippet.value = data.replace('<workspace-id>', currentWorkspace.value.id);
});

// Generate an error message for an input

const greetingErrorMessage = computed(() => VuelidateUtil.getErrorMessage($v, 'greetingText', 'Greeting text'));
const introductionErrorMessage = computed(() =>
  VuelidateUtil.getErrorMessage($v, 'introductionText', 'Introduction text'),
);

function reinstall() {
  if (reinstallInProgress.value) {
    return;
  }

  reinstallInProgress.value = true;

  webAssistantAppService
    .fullInstall(currentWorkspace.value.id, currentWorkspace.value.name, viewModel.value)
    .then((response) => {
      viewModel.value = response as WebAssistantViewModel;
      identityVerificationSecret.value = response.identityVerificationSecret;
      Toast.showSuccess('Web Assistant has been successfully configured');
      webAssistantAppInstallError.value = false;
    })
    .catch((error) => {
      Toast.showError(error.message);
    })
    .finally(() => {
      reinstallInProgress.value = false;
    });
}

function update() {
  if ($v.value.$invalid) {
    return;
  }

  if (imageUploading.value) {
    Toast.showInfo('Cannot save while an image is being uploaded, please try again');
    return;
  }

  if (!updateInProgress.value && !reinstallInProgress.value) {
    updateInProgress.value = true;

    webAssistantApi
      .updateWebAssistant(currentWorkspace.value.id, viewModel.value)
      .then(() => Toast.showSuccess('Successfully updated Web Assistant'))
      .catch((err) => Toast.showError(err.message))
      .finally(() => (updateInProgress.value = false));
  }
}

function onImageSelected() {
  imageUploading.value = true;
}

function onImageUploaded(imageUrl: string) {
  viewModel.value.imageUrl = imageUrl;
  imageUploading.value = false;
}

function onImageCleared() {
  viewModel.value.imageUrl = '';
  imageUploading.value = false;
}

function getCodeSnippet() {
  return '<script>' + codeSnippet.value + '<\/script>';
}

function getInitSnippet() {
  return `<script>Connect.init({ workspaceId: '${currentWorkspace.value.id}' });<\/script>`;
}

function configRequest(workspaceId: string, installedAppId: string, inputData: any, storedData: any, componentId: any) {
  return webAssistantApi.homescreenAppConfig(workspaceId, installedAppId, inputData, storedData, componentId);
}

function homescreenAppsChanged(apps: HomescreenAppViewModel[]) {
  const promises = apps.map((app) => {
    return homescreenAppCache.value
      ?.initialiseApp(currentWorkspace.value.id, app)
      .then((response: HomescreenAppInitViewModel) => {
        return { configured: app, initialised: response };
      })
      .catch((error: any) => {
        Toast.showError('Failed to load homescreen app');
        const init: HomescreenAppInitViewModel = {
          id: `${app.installedAppId}`,
          card: {
            components: [],
          },
        };
        return { configured: app, initialised: init };
      });
  });

  Promise.all(promises).then((res) => {
    viewModel.value.homescreenApps = res.map(
      (x: { configured: HomescreenAppViewModel; initialised: HomescreenAppInitViewModel }) => x!.configured,
    );
    initialisedHomescreenApps.value = res.map(
      (x: { configured: HomescreenAppViewModel; initialised: HomescreenAppInitViewModel }) => x!.initialised,
    );
  });
}

function messageLoad($state: any) {
  $state.loaded();
  $state.complete();
}

function handleAppUpdate(app: HomescreenAppViewModel, appIndex: number) {
  viewModel.value.homescreenApps[appIndex] = app;
  homescreenAppsChanged(viewModel.value.homescreenApps);
}

// Whether an input is currently invalid because it is required and empty.
function invalidRequired($v: any, prop: string) {
  return !$v.viewModel[prop].required && $v.viewModel[prop].$invalid && $v.viewModel[prop].$dirty;
}

// Whether an input is currently invalid because it is longer than the maximum allowed.
function invalidMaxLength($v: any, prop: string) {
  return !$v.viewModel[prop].maxLength && $v.viewModel[prop].$invalid && $v.viewModel[prop].$dirty;
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.columns__column--no-top-padding {
  padding-top: 0;

  strong {
    margin-bottom: 0;

    &:not(:first-child) {
      margin-top: 2rem;
    }
  }
}

.preview-column {
  padding-top: 140px;
}

.preview-container {
  position: sticky;
  top: 100px;

  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  .buttons {
    margin-bottom: 1rem;
  }
}

.code-wrapper {
  overflow-x: auto;
  background: #f5f5f5;
  font-family: monospace;
  width: 100%;
  padding: 1em;
  margin-bottom: 1em;
  max-height: 6em;

  code {
    background: #f5f5f5;
    color: #4a4a4a;
  }
}

.circle-image {
  background-color: white;
  border-radius: 99em;
  width: 3em;
  height: 3em;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;

  * {
    width: 100%;
    height: 100%;
  }
}

.launcher-label {
  display: inline-block;
  padding-right: 20px;
}

:deep(.home-container) {
  .fresco-panel {
    pointer-events: none;
  }
}
</style>
